import { Dispatch, useEffect } from "react";
import { Col, Row, Form } from "react-bootstrap";
import styled from "@emotion/styled";

import TimeInput from "./TimeInput";
import { Actuator, ActuatorTiming } from "types/Actuator";
import { TimeFormat } from "types/TimeInput";
import styledConst from "styles";

export type MinuteSecondPickerProps = {
    editedActuator: Actuator;
    /** @deprecated use onChange instead */
    setEditedActuator?: Dispatch<React.SetStateAction<[]>>;
    onChange?: (timing: ActuatorTiming) => void;
    hasCycles?: boolean;
    cycleIndex?: number;
    format?: TimeFormat;
    variant?: "default" | "light";
};

const StyledRow = styled(Row)`
    &.light {
        .minuteSecondsField {
            background: #f0f0f0 !important;
            border: none;
            color: ${styledConst.Primary_Blue_3};
            max-width: none;

            .MuiInputBase-input {
                padding-top: 7px;
                padding-bottom: 7px;
            }
        }
    }
`;

const MinuteSecondPicker = (props: MinuteSecondPickerProps) => {
    const {
        editedActuator: { timing },
        setEditedActuator,
        onChange,
        hasCycles,
        cycleIndex,
        format,
        variant = "default",
    } = props;

    useEffect(() => {
        setEditedActuator?.((prevState) => ({
            ...prevState,
            timing: timing || [],
        }));
        onChange?.(timing || []);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setEditedActuator, timing]);

    const renderMinuteSecondFields = () => {
        if (hasCycles) {
            const i = Number(cycleIndex) - 1;
            return (
                <>
                    <Col>
                        <Row>
                            <Col
                                xs={2}
                                className="timeFields minuteSecondsFieldLabel"
                            >
                                <Form.Label className="mt-2">
                                    {cycleIndex}-T1
                                </Form.Label>
                            </Col>
                            <Col xs={10} className="timeFields">
                                <TimeInput
                                    className="minuteSecondsField"
                                    id={`time-input-t1-${cycleIndex}`}
                                    format={format ?? "mm:ss"}
                                    format_type="split"
                                    value={timing?.[i]?.[0]}
                                    inputProps={{
                                        min: 0,
                                    }}
                                    onChange={(a) => {
                                        if (timing) {
                                            const updatedTiming = [...timing];
                                            updatedTiming[i] = [
                                                a,
                                                timing?.[i]?.[1] ?? 0,
                                            ];
                                            setEditedActuator?.(
                                                (prevState) => ({
                                                    ...prevState,
                                                    timing: updatedTiming,
                                                })
                                            );
                                            onChange?.(updatedTiming);
                                        }
                                    }}
                                />
                            </Col>
                        </Row>
                    </Col>

                    <Col>
                        <Row>
                            <Col xs={2} className="minuteSecondsFieldLabel">
                                <Form.Label className="mt-2 ">
                                    {cycleIndex}-T2
                                </Form.Label>
                            </Col>
                            <Col xs={10} className="timeFields">
                                <TimeInput
                                    className="minuteSecondsField"
                                    id={`time-input-t2-${cycleIndex}`}
                                    format={format ?? "mm:ss"}
                                    format_type="split"
                                    value={timing?.[i]?.[1]}
                                    inputProps={{
                                        min: 0,
                                    }}
                                    onChange={(b) => {
                                        if (timing) {
                                            const updatedTiming = [...timing];
                                            updatedTiming[i] = [
                                                timing?.[i]?.[0] ?? 0,
                                                b,
                                            ];
                                            setEditedActuator?.(
                                                (prevState) => ({
                                                    ...prevState,
                                                    timing: updatedTiming,
                                                })
                                            );
                                            onChange?.(updatedTiming);
                                        }
                                    }}
                                />
                            </Col>
                        </Row>
                    </Col>
                </>
            );
        } else {
            return (
                <Col>
                    <Form.Row className="mt-4 mb-1 ">
                        <Col md={1} className="minuteSecondsFieldLabel">
                            <Form.Label className="mt-2">T1</Form.Label>
                        </Col>
                        <Col md={11}>
                            <TimeInput
                                id="time-input-t1"
                                className="minuteSecondsField"
                                format={format ?? "mm:ss"}
                                format_type="split"
                                value={timing?.[0]?.[0] ?? 0}
                                inputProps={{
                                    min: 0,
                                }}
                                onChange={(a) => {
                                    setEditedActuator?.((prevState) => ({
                                        ...prevState,
                                        timing: [[a, timing?.[0]?.[1] ?? 0]],
                                    }));
                                    onChange?.([[a, timing?.[0]?.[1] ?? 0]]);
                                }}
                            />
                        </Col>
                    </Form.Row>
                    <Form.Row className="mt-1 mb-1">
                        <Col md={1} className="minuteSecondsFieldLabel">
                            <Form.Label className="mt-2 ">T2</Form.Label>
                        </Col>
                        <Col md={11}>
                            <TimeInput
                                id="time-input-t2"
                                className="minuteSecondsField"
                                format={format ?? "mm:ss"}
                                format_type="split"
                                value={timing?.[0]?.[1] ?? 0}
                                inputProps={{
                                    min: 0,
                                }}
                                onChange={(b) => {
                                    setEditedActuator?.((prevState) => ({
                                        ...prevState,
                                        timing: [[timing?.[0]?.[0] ?? 0, b]],
                                    }));
                                    onChange?.([[timing?.[0]?.[0] ?? 0, b]]);
                                }}
                            />
                        </Col>
                    </Form.Row>
                </Col>
            );
        }
    };

    return (
        <StyledRow className={`w-100 ${variant === "light" ? "light" : ""}`}>
            {renderMinuteSecondFields()}
        </StyledRow>
    );
};

export default MinuteSecondPicker;
